import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router";
import Modal from "@material-ui/core/Modal";
import Fade from "@material-ui/core/Fade";

import DatePicker from "../../../../../copms/DatePicker/DatePicker";
import currencyFilter from "../../../../../helpers/currencyFilter";
import Loading from "../../../Loading/Loading";
import { request } from "../../../../../config";
import { getFormatedDate } from "../../../../../helpers/fotmatDate";

import "./CardLimitsForm.scss";

let countryFlagsCache;
let countryFlagsRequest;

const getCountryList = (response) => {
  if (Array.isArray(response)) return response;

  return (
    response?.countryList ||
    response?.countries ||
    response?.cardLimitCountryList ||
    response?.data ||
    []
  );
};

const getCountryCode = (country) =>
  country?.countryCode || country?.countryISO || country?.countryIso || country?.isoCode || country?.code;

const getCountryName = (country) =>
  country?.countryName || country?.name || country?.country || getCountryCode(country);

const getCountryFlag = (country) =>
  country?.flag || country?.flagUrl || country?.flagImage || country?.imageUrl || country?.image;

const getFlagSrc = (flag) => {
  if (!flag) return flag;
  if (/^(data:|https?:)/i.test(flag)) return flag;
  return `data:image/png;base64,${flag}`;
};

const CardLimitsForm = ({
  singleCardLimit: {
    name,
    defaultLimit,
    currentLimitCount,
    currencyLimit,
    maxLimit,
    limitCodeId,
    limit_rule: limitRule,
    typeId,
  },
  setOpenForm,
  logout,
  alertState,
  updateAppState,
  t,
}) => {
  const { id, currency } = useParams();

  const [inputValue, setInputValue] = useState();
  const [loading, setLoading] = useState(true);
  const [cardLimit, setCardLimit] = useState();
  const [activationDate, setActivationDate] = useState();
  const [selectedDate, setSelectedDate] = useState(null);
  const [amountErrMsg, setAmountErrMsg] = useState();
  const [securityLimitType, setSecurityLimitType] = useState("standard");
  const [operationCount, setOperationCount] = useState();
  const [hasIndividualLimit, setHasIndividualLimit] = useState(false);
  const [riskAccepted, setRiskAccepted] = useState(false);
  const [countriesModalOpen, setCountriesModalOpen] = useState(false);
  const [individualLimitModalOpen, setIndividualLimitModalOpen] = useState(false);
  const [countryList, setCountryList] = useState([]);
  // const [countriesLoading, setCountriesLoading] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");

  let today = new Date();
  const isSecurityLimit = typeId === 1;
  const hasIndividualAmount = Number(limitCodeId) !== 33;
  const cardCurrency = currency || currencyLimit;

  const {
    register,
    handleSubmit,
    reset,
    control,
  } = useForm();

  const cancelForm = () => {
    setOpenForm(false);
  };

  const getCountryFlags = () => {
    if (countryFlagsCache) return Promise.resolve(countryFlagsCache);
    if (countryFlagsRequest) return countryFlagsRequest;

    countryFlagsRequest = fetch(request("card-limit-countries/flags", { token: localStorage.getItem("token") }, "POST"))
      .then((res) => res.text())
      .then((responseText) => {
        const JSON = require("true-json-bigint");
        const response = JSON.parse(responseText);
        countryFlagsCache = getCountryList(response);
        return countryFlagsCache;
      })
      .catch((error) => {
        countryFlagsRequest = null;
        throw error;
      });

    return countryFlagsRequest;
  };

  const openCountriesModal = () => {
    // setCountriesLoading(true);
    updateAppState("isLoadingTransparentBackground", true);
    setCountrySearch("");

    Promise.all([
      getCountryFlags(),
      fetch(request(`card-limit-countries/by-limit-code`, { token: localStorage.getItem("token"), limitCodeId }, "POST"))
        .then((res) => res.text())
        .then((responseText) => {
          const JSON = require("true-json-bigint");
          return getCountryList(JSON.parse(responseText));
        }),
    ])
      .then(([flags, countries]) => {
        const flagsByCode = flags.reduce((result, flag) => {
          const code = getCountryCode(flag);
          if (code) result[String(code).toUpperCase()] = flag;
          return result;
        }, {});

        setCountryList(
          countries.map((country) => {
            const countryCode = getCountryCode(country);
            const matchingFlag = flagsByCode[String(countryCode || "").toUpperCase()];

            return {
              code: countryCode,
              name: getCountryName(country),
              flag: getFlagSrc(getCountryFlag(matchingFlag) || getCountryFlag(country)),
            };
          })
        );
        setCountriesModalOpen(true);
      })
      .catch(() => {
        setCountriesModalOpen(false);
        alertState(true, "error", t("an error occurred"));
      })
      .finally(() => updateAppState("isLoadingTransparentBackground", false));
  };

  const getCardLimitData = () => {
    setLoading(true);
    const requestBody = {
      custId: parseInt(localStorage.getItem("custId")),
      cardId: parseInt(id),
      limitCodeId: limitCodeId,
      token: localStorage.getItem("token"),
    };

    fetch(request("card/getCardLimitData", requestBody))
      .then((res) => res.text())
      .then((getCardLimitDataResponseJSON) => {
        const JSON = require("true-json-bigint");
        let getCardLimitDataResponse = JSON.parse(getCardLimitDataResponseJSON);
        if (getCardLimitDataResponse?.status?.statusCode !== 1) {
          if (getCardLimitDataResponse?.status?.statusCode === 200) {
            logout();
          } else {
            setLoading(false);
            alertState(
              true,
              "error",
              t("an error occurred"),
              t(getCardLimitDataResponse?.status?.statusCode)
            );
            setOpenForm(false);
          }
        } else {

          setCardLimit({ ...getCardLimitDataResponse });
          setActivationDate(getCardLimitDataResponse?.activationDate);
          if (isSecurityLimit) {
            const hasExistingIndividualLimit =
              getCardLimitDataResponse?.isStandard === false;
            setSecurityLimitType(
              hasExistingIndividualLimit ? "individual" : "standard"
            );
            setHasIndividualLimit(hasExistingIndividualLimit);
            setInputValue(
              hasExistingIndividualLimit
                ? getCardLimitDataResponse?.currentAmount
                : undefined
            );
            setOperationCount(getCardLimitDataResponse?.maxNumber);
            setSelectedDate(getCardLimitDataResponse?.dateTo || null);
          }
        }
      })
      .finally(() => setLoading(false));
  };

  const closeSuccessFunction = () => {
    return window.location.reload();
  };

  const onSubmit = (data) => {
    updateAppState("isLoadingTransparentBackground", true);
    const isStandard = isSecurityLimit
      ? securityLimitType === "standard"
      : false;
    let requestBody = {
      custId: parseInt(localStorage.getItem("custId")),
      cardId: parseInt(id),
      limitCodeId: limitCodeId,
      currentAmount: isStandard ? defaultLimit : hasIndividualAmount ? Number(inputValue) : cardLimit?.currentAmount,
      dateFrom: getFormatedDate(today, "-"),
      dateTo: selectedDate || null,
      maxNumber: isStandard ? currentLimitCount : Number(operationCount),
      isStandard,
      token: localStorage.getItem("token"),
    };

    fetch(request("card/usageLimitactivation", requestBody))
      .then((res) => res.text())
      .then((limitActivationResponseJSON) => {
        const JSON = require("true-json-bigint");
        let limitActivationResponse = JSON.parse(limitActivationResponseJSON);
        if (limitActivationResponse?.status?.statusCode !== 1) {
          if (limitActivationResponse?.status?.statusCode === 200) {
            logout();
          } else {
            updateAppState("isLoadingTransparentBackground", false);
            alertState(
              true,
              "error",
              t("an error occurred"),
              t(limitActivationResponse?.status?.statusCode)
            );
          }
        } else {
          updateAppState("isLoadingTransparentBackground", false);
          alertState(
            true,
            "success",
            t("successful operation"),
            t("Limit has been successfully changed"),
            null,
            false,
            closeSuccessFunction
          );
          reset();
        }
      });
  };

  const setAmount = (amount) => {
    if (Number(amount) === Number(cardLimit?.currentAmount)) {
      setAmountErrMsg(
        `* ${t("Enter a limit different from the current limit")}`
      );
    } else if (amount > maxLimit) {
      setAmountErrMsg(`* ${t("Unable to set maximum limit")}`);
    } else {
      setInputValue(amount);
      setAmountErrMsg("");
      setActivationDate(null);
    }
  };
  const setOperationNumber = (amount) => {
    const maxLimit = 5
    if (amount > maxLimit) {
      setAmountErrMsg(`* ${t("Unable to set number of operation")}`);
    } else {
      setOperationCount(amount);
      setAmountErrMsg("");
      setActivationDate(null);
    }
  };

  const securityFormIsValid =
    riskAccepted &&
    (securityLimitType === "standard" ||
      (hasIndividualLimit && (!hasIndividualAmount || inputValue) && operationCount && !amountErrMsg && selectedDate));

  const openIndividualLimitModal = () => {
    setIndividualLimitModalOpen(true);
  };

  const selectIndividualLimit = () => {
    setSecurityLimitType("individual");
    setHasIndividualLimit(true);
    openIndividualLimitModal();
  };

  const closeIndividualLimitModal = () => {
    setIndividualLimitModalOpen(false);
  };

  const filteredCountryList = countryList.filter(({ name, code }) => {
    const searchValue = countrySearch.toLowerCase();
    return (
      String(name || "").toLowerCase().includes(searchValue) ||
      String(code || "").toLowerCase().includes(searchValue)
    );
  });

  useEffect(() => {
    getCardLimitData();
    reset();
  }, [limitCodeId, reset]);

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className="ibank-card-limits__form__content">
          {isSecurityLimit ? (
            <form
              className="ibank-card-limits__form__content__security-form"
              onSubmit={
                securityLimitType === "standard"
                  ? (event) => {
                    event.preventDefault();
                    onSubmit({});
                  }
                  : handleSubmit(onSubmit)
              }
            >
              <div className="ibank-card-limits__form__content__security-form__heading">
                <p>{t("Your selection")}</p>
                <button
                  type="button"
                  className="ibank-card-limits__form__content__security-form__countries"
                  onClick={openCountriesModal}
                >
                  {t("seeCountriesCount", {
                    count: cardLimit?.countryCount || 0,
                  })}
                </button>
              </div>
              <label className="ibank-card-limits__form__content__security-form__option">
                <input
                  type="radio"
                  name="securityLimitType"
                  checked={securityLimitType === "standard"}
                  onChange={() => {
                    setSecurityLimitType("standard");
                    setHasIndividualLimit(false);
                  }}
                />
                <span>{t("Standard limit")}</span>
              </label>
              {securityLimitType === "standard" && (
                <p className="ibank-card-limits__form__content__security-form__rule">
                  {limitRule || `${currentLimitCount || 0} əməliyyat və ${defaultLimit || 0} ${currencyFilter(cardCurrency)} limiti tətbiq olunur.`}
                </p>
              )}
              <label className="ibank-card-limits__form__content__security-form__option">
                <input
                  type="radio"
                  name="securityLimitType"
                  checked={securityLimitType === "individual"}
                  onChange={selectIndividualLimit}
                />
                <span>{t("individualLimitTitle")}</span>
              </label>
              {securityLimitType === "individual" && hasIndividualLimit && (
                <div className="ibank-card-limits__form__content__security-form__summary">
                  <button
                    type="button"
                    onClick={openIndividualLimitModal}
                    aria-label="Fərdi limiti redaktə et"
                  >
                    &#9998;
                  </button>
                  <p><span>{t("operationCount")}</span>{operationCount || "-"}</p>
                  {hasIndividualAmount && <p><span>{t("amount")}</span>{inputValue || "-"} {currencyFilter("USD")}</p>}
                  <p><span>{t("activationDate")}</span>{getFormatedDate(today, "dd.mm.yyyy") || "-"}</p>
                  <p><span>{t("deactivationDate")}</span>{getFormatedDate(selectedDate, "dd.mm.yyyy") || "-"}</p>
                </div>
              )}
              <label className="ibank-card-limits__form__content__security-form__consent">
                <input
                  type="checkbox"
                  checked={riskAccepted}
                  onChange={(event) => setRiskAccepted(event.target.checked)}
                />
                <span>{t("limitChangeRisks")}</span>
              </label>
              <div className="ibank-card-limits__form__content__security-form__actions">
                <button type="submit" disabled={!securityFormIsValid}>
                  {t("saveLimitAction")}
                </button>
              </div>
            </form>
          ) : (
            <>
              <p className="ibank-card-limits__form__content__header">{name}</p>
              <div className="ibank-card-limits__form__content__types">
                <div className="ibank-card-limits__form__content__types__item">
                  <p className="ibank-card-limits__form__content__types__item__title">
                    {t("Standard limit")}
                  </p>
                  <p className="ibank-card-limits__form__content__types__item__amount">
                    {defaultLimit} {currencyFilter(cardCurrency)}
                  </p>
                </div>
                <div className="ibank-card-limits__form__content__types__item">
                  <p className="ibank-card-limits__form__content__types__item__title">
                    {t("Maximum limit")}
                  </p>
                  <p className="ibank-card-limits__form__content__types__item__amount">
                    {maxLimit} {currencyFilter(cardCurrency)}
                  </p>
                </div>
                <div className="ibank-card-limits__form__content__types__item">
                  <p className="ibank-card-limits__form__content__types__item__title">
                    {t("Current limit")}
                  </p>
                  <p className="ibank-card-limits__form__content__types__item__amount">
                    {cardLimit?.currentAmount} {currencyFilter(cardCurrency)}
                  </p>
                </div>
              </div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="ibank-card-limits__form__content__inputs">
                  <div className="dashboard-payment-container-data-section-content-container-form-input-group__grid dashboard-payment-container-data-section-content-container-form-input-group__grid">
                    <label className="dashboard-payment-container-data-section-content-container-form-label">
                      {t("Define a new limit")}
                    </label>
                    <div
                      className={`dashboard-payment-container-data-section-content-container-form-input-box ${amountErrMsg && "input-outline"
                        }`}
                    >
                      <input
                        {...register("amount", { required: true })}
                        onChange={(e) => {
                          setAmount(e?.target.value);
                        }}
                        placeholder={t("Amount")}
                        type="number"
                        onKeyDown={(event) => {
                          if (event.key === ".") {
                            event.preventDefault();
                          }
                        }}
                        onInput={(event) =>
                        (event.target.value = event.target.value.replace(
                          /[^0-9]*/g,
                          ""
                        ))
                        }
                        className="dashboard-payment-container-data-section-content-container-form-input"
                      />
                      <div className="dashboard-payment-container-data-section-content-container-form-icon dashboard-payment-container-data-section-content-container-form-icon__currency">
                        {cardCurrency.toUpperCase()}
                      </div>
                    </div>
                    {amountErrMsg && (
                      <p className="ibank-input__err__msg">{amountErrMsg}</p>
                    )}
                  </div>
                  <div className="dashboard-payment-container-data-section-content-container-form-input-group dashboard-payment-container-data-section-content-container-form-input-group__grid ibank-card-limits__form__content__datepicker">
                    <div className="dashboard-payment-container-data-section-content-container-form-input-box">
                      <Controller
                        control={control}
                        name={"activationDate"}
                        render={({ field }) => (
                          <DatePicker
                            format="dd-MM-yyyy"
                            disablePast={true}
                            error={false}
                            placeholder={t("Choose")}
                            fillColor="#0179D2"
                            selectedDate={activationDate}
                            updateDate={(e) => {
                              let date = getFormatedDate(e, "-");
                              setSelectedDate(date);
                            }}
                          />
                        )}
                      />
                    </div>
                    <p className="ibank-input__err__msg">
                      {t(
                        "If you do not select a date, then the limit is valid until the expiration date of the card"
                      )}
                    </p>
                  </div>
                </div>
                <div className="ibank-card-limits__form__content__actions">
                  <button
                    type="submit"
                    className={`dashboard-payment-container-data-section-content-container-form-footer-button dashboard-payment-container-data-section-content-container-form-footer-button__${!inputValue || amountErrMsg ? "disable" : "blue"
                      } dashboard-payment-container-data-section-content-container-form-footer-button__left`}
                    disabled={!inputValue || amountErrMsg}
                  >
                    {t("save")}
                  </button>
                  <button
                    type="button"
                    className="dashboard-payment-container-data-section-content-container-form-footer-button dashboard-payment-container-data-section-content-container-form-footer-button__gray dashboard-payment-container-data-section-content-container-form-footer-button__right"
                    onClick={cancelForm}
                  >
                    {t("refusal")}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      )}
      <Modal
        open={countriesModalOpen}
        onClose={() => setCountriesModalOpen(false)}
        className="ibank-card-limits__countries-modal"
      >
        <Fade in={countriesModalOpen}>
          <div className="ibank-card-limits__countries-modal__content">
            <button
              type="button"
              className="ibank-card-limits__countries-modal__close"
              onClick={() => setCountriesModalOpen(false)}
              aria-label={t("close")}
            >
              x
            </button>
            <h3>{t("countriesTitle")}</h3>
            <input
              type="search"
              value={countrySearch}
              onChange={(event) => setCountrySearch(event.target.value)}
              placeholder="Ölkə axtar"
              aria-label="Ölkə axtar"
            />
            <div className="ibank-card-limits__countries-modal__list">
              {
                // countriesLoading ? (
                //   <p>{t("Loading")}</p>
                // ) : (
                filteredCountryList.map(({ code, name, flag }) => (
                  <div className="ibank-card-limits__countries-modal__item" key={code || name}>
                    {flag ? <img src={flag} alt="" /> : <span className="ibank-card-limits__countries-modal__flag-placeholder" />}
                    <span>{name}</span>
                  </div>
                ))
                // )
              }
            </div>
            <button
              type="button"
              className="ibank-card-limits__countries-modal__action"
              onClick={() => setCountriesModalOpen(false)}
            >
              {t("close")}
            </button>
          </div>
        </Fade>
      </Modal>
      <Modal
        open={individualLimitModalOpen}
        onClose={closeIndividualLimitModal}
        className="ibank-card-limits__individual-limit-modal"
      >
        <Fade in={individualLimitModalOpen}>
          <div className="ibank-card-limits__individual-limit-modal__content">
            <button
              type="button"
              className="ibank-card-limits__individual-limit-modal__close"
              onClick={closeIndividualLimitModal}
              aria-label={t("close")}
            >
              x
            </button>
            <h3>{t("setIndividualLimit")}</h3>
            <p className="ibank-card-limits__individual-limit-modal__intro">
              {t("setIndividualLimitHint")}
            </p>
            <div className="ibank-card-limits__individual-limit-modal__fields">
              <label>
                {t("operationCount")}
                <input
                  {...register("operationCount", { required: true })}
                  value={operationCount || ""}
                  onChange={(event) => setOperationNumber(event.target.value)}
                  type="number"
                  min="1"
                  max="5"
                  placeholder="Seçim edin"
                />
              </label>
              {hasIndividualAmount && (
                <label>
                  {t("Amount")}
                  <input
                    {...register("amount", { required: true })}
                    value={inputValue || ""}
                    onChange={(event) => setAmount(event.target.value)}
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Daxil edin"
                  />
                </label>
              )}
              <label>
                {t("dateRange")} <span>*</span>
                <Controller
                  control={control}
                  name="securityDate"
                  render={() => (
                    <DatePicker
                      format="dd-MM-yyyy"
                      disablePast={true}
                      error={false}
                      placeholder="Daxil edin və ya təqvimdən seçin"
                      fillColor="#0179D2"
                      selectedDate={selectedDate}
                      updateDate={(date) => setSelectedDate(getFormatedDate(date, "-"))}
                    />
                  )}
                />
              </label>
              {amountErrMsg && <p className="ibank-input__err__msg">{amountErrMsg}</p>}
              <p className="ibank-card-limits__individual-limit-modal__notice">
                {t("limitAutoActivateNote")}
              </p>
            </div>
            <button
              type="button"
              className="ibank-card-limits__individual-limit-modal__action"
              onClick={closeIndividualLimitModal}
            >
              {t("close")}
            </button>
          </div>
        </Fade>
      </Modal>
    </>
  );
};

export default CardLimitsForm;
